/**
 * Advanced Activity Filters
 *
 * PE-877: Multi-select filters for User, Source App, and Date Range
 * Extends the basic type filter with advanced filtering capabilities.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Filter, X, ChevronDown, User, Calendar, AppWindow, Check } from 'lucide-react';
import { ActivityType, SourceApp } from '../../types/activity';
import { AdvancedFilterState } from '../../hooks/useActivities';

interface AdvancedActivityFiltersProps {
  filters: AdvancedFilterState;
  onTypeChange: (type: ActivityType | 'all') => void;
  onUserChange: (userIds: string[]) => void;
  onSourceAppChange: (sourceApps: SourceApp[]) => void;
  onDateRangeChange: (dateFrom: string | null, dateTo: string | null) => void;
  onClearFilters: () => void;
  availableUsers: { id: string; name: string }[];
  availableSourceApps: SourceApp[];
  hasActiveFilters: boolean;
  activeFilterCount: number;
}

const typeFilterOptions: { value: ActivityType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'email', label: 'Emails' },
  { value: 'call', label: 'Calls' },
  { value: 'sms', label: 'SMS' },
  { value: 'meeting', label: 'Meetings' },
  { value: 'task', label: 'Tasks' },
  { value: 'note', label: 'Notes' },
];

const sourceAppLabels: Record<SourceApp, string> = {
  regal_main: 'Regal Main',
  credit_management: 'Credit Management',
  cybersweep: 'CyberSweep',
  credit_block: 'Credit Block',
};

export const AdvancedActivityFilters: React.FC<AdvancedActivityFiltersProps> = ({
  filters,
  onTypeChange,
  onUserChange,
  onSourceAppChange,
  onDateRangeChange,
  onClearFilters,
  availableUsers,
  availableSourceApps,
  hasActiveFilters,
  activeFilterCount,
}) => {
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const userDropdownRef = useRef<HTMLDivElement>(null);
  const sourceDropdownRef = useRef<HTMLDivElement>(null);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
      if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(event.target as Node)) {
        setShowSourceDropdown(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleUser = (userId: string) => {
    const newUserIds = filters.userIds.includes(userId)
      ? filters.userIds.filter(id => id !== userId)
      : [...filters.userIds, userId];
    onUserChange(newUserIds);
  };

  const toggleSourceApp = (app: SourceApp) => {
    const newApps = filters.sourceApps.includes(app)
      ? filters.sourceApps.filter(a => a !== app)
      : [...filters.sourceApps, app];
    onSourceAppChange(newApps);
  };

  const getUserButtonLabel = () => {
    if (filters.userIds.length === 0) return 'All Users';
    if (filters.userIds.length === 1) {
      const user = availableUsers.find(u => u.id === filters.userIds[0]);
      return user?.name || 'User';
    }
    return `${filters.userIds.length} Users`;
  };

  const getSourceAppButtonLabel = () => {
    if (filters.sourceApps.length === 0) return 'All Apps';
    if (filters.sourceApps.length === 1) {
      return sourceAppLabels[filters.sourceApps[0]];
    }
    return `${filters.sourceApps.length} Apps`;
  };

  const getDateRangeLabel = () => {
    if (!filters.dateFrom && !filters.dateTo) return 'All Time';
    if (filters.dateFrom && filters.dateTo) {
      return `${formatDateShort(filters.dateFrom)} - ${formatDateShort(filters.dateTo)}`;
    }
    if (filters.dateFrom) return `From ${formatDateShort(filters.dateFrom)}`;
    if (filters.dateTo) return `Until ${formatDateShort(filters.dateTo)}`;
    return 'All Time';
  };

  const formatDateShort = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Filter Icon */}
      <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
        <Filter className="w-4 h-4" />
        {activeFilterCount > 0 && (
          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full">
            {activeFilterCount}
          </span>
        )}
      </div>

      {/* Type Filter */}
      <select
        value={filters.typeFilter}
        onChange={(e) => onTypeChange(e.target.value as ActivityType | 'all')}
        className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {typeFilterOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* User Filter (Multi-select) */}
      {availableUsers.length > 0 && (
        <div className="relative" ref={userDropdownRef}>
          <button
            onClick={() => setShowUserDropdown(!showUserDropdown)}
            className={`flex items-center gap-1.5 text-sm border rounded-md px-2 py-1.5 transition-colors ${
              filters.userIds.length > 0
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
            } hover:border-blue-500 dark:hover:border-blue-500`}
          >
            <User className="w-3.5 h-3.5" />
            <span>{getUserButtonLabel()}</span>
            <ChevronDown className="w-3.5 h-3.5" />
          </button>

          {showUserDropdown && (
            <div className="absolute z-20 mt-1 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="py-1 max-h-60 overflow-y-auto">
                {availableUsers.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => toggleUser(user.id)}
                    className="w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                  >
                    <span>{user.name}</span>
                    {filters.userIds.includes(user.id) && (
                      <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    )}
                  </button>
                ))}
              </div>
              {filters.userIds.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2">
                  <button
                    onClick={() => onUserChange([])}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    Clear selection
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Source App Filter (Multi-select) */}
      {availableSourceApps.length > 0 && (
        <div className="relative" ref={sourceDropdownRef}>
          <button
            onClick={() => setShowSourceDropdown(!showSourceDropdown)}
            className={`flex items-center gap-1.5 text-sm border rounded-md px-2 py-1.5 transition-colors ${
              filters.sourceApps.length > 0
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
            } hover:border-blue-500 dark:hover:border-blue-500`}
          >
            <AppWindow className="w-3.5 h-3.5" />
            <span>{getSourceAppButtonLabel()}</span>
            <ChevronDown className="w-3.5 h-3.5" />
          </button>

          {showSourceDropdown && (
            <div className="absolute z-20 mt-1 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700">
              <div className="py-1">
                {availableSourceApps.map((app) => (
                  <button
                    key={app}
                    onClick={() => toggleSourceApp(app)}
                    className="w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                  >
                    <span>{sourceAppLabels[app]}</span>
                    {filters.sourceApps.includes(app) && (
                      <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    )}
                  </button>
                ))}
              </div>
              {filters.sourceApps.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2">
                  <button
                    onClick={() => onSourceAppChange([])}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    Clear selection
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Date Range Filter */}
      <div className="relative" ref={datePickerRef}>
        <button
          onClick={() => setShowDatePicker(!showDatePicker)}
          className={`flex items-center gap-1.5 text-sm border rounded-md px-2 py-1.5 transition-colors ${
            filters.dateFrom || filters.dateTo
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
          } hover:border-blue-500 dark:hover:border-blue-500`}
        >
          <Calendar className="w-3.5 h-3.5" />
          <span>{getDateRangeLabel()}</span>
          <ChevronDown className="w-3.5 h-3.5" />
        </button>

        {showDatePicker && (
          <div className="absolute z-20 mt-1 right-0 w-72 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => onDateRangeChange(e.target.value || null, filters.dateTo)}
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => onDateRangeChange(filters.dateFrom, e.target.value || null)}
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    // Quick preset: Last 7 days
                    const today = new Date();
                    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                    onDateRangeChange(weekAgo.toISOString().split('T')[0], today.toISOString().split('T')[0]);
                  }}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Last 7 days
                </button>
                <button
                  onClick={() => {
                    // Quick preset: Last 30 days
                    const today = new Date();
                    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                    onDateRangeChange(monthAgo.toISOString().split('T')[0], today.toISOString().split('T')[0]);
                  }}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Last 30 days
                </button>
                <button
                  onClick={() => onDateRangeChange(null, null)}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Clear All Filters */}
      {hasActiveFilters && (
        <button
          onClick={onClearFilters}
          className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
          Clear all
        </button>
      )}
    </div>
  );
};
